import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useConfigStore } from '../../../store/configStore';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';

export default function LegacySeatRegister() {
  const { hasPermission } = useAuthStore();
  const { seats, stations, trains, isLoading, error, fetchSeats, fetchStations, fetchTrains, createSeat, updateSeat, deleteSeat, clearError } = useConfigStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSeat, setEditingSeat] = useState(null);
  const [filterRailCode, setFilterRailCode] = useState('');
  const [filterHocha, setFilterHocha] = useState('');
  const [formData, setFormData] = useState({
    s_seq_num: '',
    ctm_name: '',
    rail_code: '',
    rail_linenum: 0,
    state: 1,
    hocha: 1,
    ticket_num: '',
  });

  const canWrite = hasPermission('config:write');

  useEffect(() => {
    fetchStations();
    fetchTrains();
  }, []);

  useEffect(() => {
    const params = {};
    if (filterRailCode) params.rail_code = filterRailCode;
    if (filterHocha) params.hocha = parseInt(filterHocha);
    fetchSeats(params);
  }, [filterRailCode, filterHocha]);

  const handleOpenModal = (seat = null) => {
    if (seat) {
      setEditingSeat(seat);
      setFormData({
        s_seq_num: seat.s_seq_num,
        ctm_name: seat.ctm_name || '',
        rail_code: seat.rail_code,
        rail_linenum: seat.rail_linenum,
        state: seat.state,
        hocha: seat.hocha,
        ticket_num: seat.ticket_num,
      });
    } else {
      setEditingSeat(null);
      setFormData({
        s_seq_num: stations[0]?.seq_num || '',
        ctm_name: '',
        rail_code: trains[0]?.rail_code || '',
        rail_linenum: 0,
        state: 1,
        hocha: 1,
        ticket_num: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSeat(null);
    setFormData({
      s_seq_num: '',
      ctm_name: '',
      rail_code: '',
      rail_linenum: 0,
      state: 1,
      hocha: 1,
      ticket_num: '',
    });
    clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        s_seq_num: parseInt(formData.s_seq_num),
        rail_linenum: parseInt(formData.rail_linenum),
        state: parseInt(formData.state),
        hocha: parseInt(formData.hocha),
      };

      if (editingSeat) {
        await updateSeat(editingSeat.seq_num, {
          ctm_name: data.ctm_name,
          state: data.state,
          hocha: data.hocha,
          ticket_num: data.ticket_num,
        });
      } else {
        await createSeat(data);
      }
      handleCloseModal();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (seqNum) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await deleteSeat(seqNum);
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const getStateBadge = (state) => {
    const stateMap = {
      0: { label: '비활성', class: 'bg-gray-100 text-gray-800' },
      1: { label: '활성', class: 'bg-green-100 text-green-800' },
      2: { label: '예약됨', class: 'bg-blue-100 text-blue-800' },
    };
    const stateInfo = stateMap[state] || { label: `상태 ${state}`, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stateInfo.class}`}>
        {stateInfo.label}
      </span>
    );
  };

  // Get unique rail codes for filter dropdown
  const uniqueRailCodes = [...new Set(trains.map(t => t.rail_code))];

  return (
    <div className="py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">보유좌석등록</h1>
        {canWrite && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            좌석 추가
          </button>
        )}
      </div>

      {/* 필터 영역 */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">열차 코드:</label>
            <select
              value={filterRailCode}
              onChange={(e) => setFilterRailCode(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              {uniqueRailCodes.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">호차:</label>
            <select
              value={filterHocha}
              onChange={(e) => setFilterHocha(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}호차</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => {
              setFilterRailCode('');
              setFilterHocha('');
            }}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            초기화
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={clearError} className="ml-2 underline">닫기</button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-500">로딩 중...</span>
          </div>
        ) : seats.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            등록된 좌석이 없습니다.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  순번
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  열차 코드
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  호차
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  좌석번호
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  정차역
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  고객명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                {canWrite && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {seats.map((seat) => (
                <tr key={seat.seq_num}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {seat.seq_num}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {seat.rail_code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {seat.hocha}호차
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {seat.ticket_num}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {seat.station_name || `역 #${seat.s_seq_num}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {seat.ctm_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStateBadge(seat.state)}
                  </td>
                  {canWrite && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(seat)}
                          className="text-blue-600 hover:text-blue-900"
                          title="수정"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(seat.seq_num)}
                          className="text-red-600 hover:text-red-900"
                          title="삭제"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal} />

            <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                {editingSeat ? '좌석 수정' : '좌석 추가'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      열차 코드 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.rail_code}
                      onChange={(e) => setFormData({ ...formData, rail_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!!editingSeat}
                    >
                      <option value="">선택하세요</option>
                      {uniqueRailCodes.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      정차역 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.s_seq_num}
                      onChange={(e) => setFormData({ ...formData, s_seq_num: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!!editingSeat}
                    >
                      <option value="">선택하세요</option>
                      {stations.map((station) => (
                        <option key={station.seq_num} value={station.seq_num}>
                          {station.station}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      호차 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.hocha}
                      onChange={(e) => setFormData({ ...formData, hocha: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n}>{n}호차</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      좌석번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.ticket_num}
                      onChange={(e) => setFormData({ ...formData, ticket_num: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="예: 1A, 2B"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      고객명
                    </label>
                    <input
                      type="text"
                      value={formData.ctm_name}
                      onChange={(e) => setFormData({ ...formData, ctm_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="고객명"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상태
                    </label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="0">비활성</option>
                      <option value="1">활성</option>
                      <option value="2">예약됨</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '처리 중...' : (editingSeat ? '수정' : '추가')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
